import { useMemo } from 'react'

import { F0Alert, F0Box, F0TimelineRow } from '@factorialco/f0-react'
import { ArrowCycle, CardPin } from '@factorialco/f0-react/icons/app'
import { TimelineItemEmployeesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import MultiTaskGroup from './MultiTaskGroup'
import {
  buildMetadata,
  calculateDaysOverdue,
  formatEntryDescription,
  getEntryIcon,
  groupTimelineEntries,
  mapTimelineStatus,
} from './helpers'
import type { ApprovalsV2ApprovalTimelineEntryFragment } from 'generated/resources/graphql'

type ReviewRequest = {
  decision?: string | null
  decisionReason?: string | null
  reviewedAt?: string | null
  reviewedById?: string | null
}

type ApprovalProcess = {
  decision?: string | null
  decisionReason?: string | null
  requesterId?: string | null
  updatedAt?: string | null
  reviewRequests?: Array<ReviewRequest | null> | null
}

interface Props {
  approvalProcess?: ApprovalProcess | null
  timelineEntries: ApprovalsV2ApprovalTimelineEntryFragment[]
}

/**
 * Read-only purchase request approval timeline rendered with the workflow
 * (procurement_v2) layout. Shares rendering logic with `WorkflowApprovalLayout`
 * but does not expose any actions, mutations, or modals — suitable for
 * surfaces like the inbox detail panel.
 */
const PurchaseRequestWorkflowTimeline = ({ approvalProcess, timelineEntries }: Props) => {
  const { employee } = useCurrentSession()

  const reviewRequests = useMemo(
    () =>
      (approvalProcess?.reviewRequests ?? []).filter(
        (reviewRequest): reviewRequest is ReviewRequest => !!reviewRequest
      ),
    [approvalProcess?.reviewRequests]
  )

  const employeeIds = useMemo(
    () => [
      ...new Set(
        timelineEntries.flatMap((entry) =>
          (entry.assignees ?? []).map((a) => a.employeeId).filter((id): id is string => id != null)
        )
      ),
    ],
    [timelineEntries]
  )

  const { data: employeesData } = useQuery(TimelineItemEmployeesDocument, {
    variables: { ids: employeeIds },
    skip: employeeIds.length === 0,
  })

  const employees = employeesData?.employees?.employeeNamesConnection?.nodes ?? []

  const visibleEntries = useMemo(
    () => timelineEntries.filter((entry) => entry.type !== 'start'),
    [timelineEntries]
  )

  const groups = useMemo(() => groupTimelineEntries(visibleEntries), [visibleEntries])

  const formatAlertDate = (timestamp: string) => {
    const date = stringToDate(timestamp, 'backend_datetime')
    return i18nDateToString(date, 'ui_short_with_time')
  }

  const isAutoRejected =
    approvalProcess?.decision === 'rejected' &&
    !approvalProcess?.decisionReason &&
    !reviewRequests.some((reviewRequest) => reviewRequest.decision === 'rejected')

  const automaticRejectionAlert =
    isAutoRejected && approvalProcess?.updatedAt ? (
      <F0Alert
        variant="critical"
        title={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.title'
        )}
        description={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.description',
          {
            date: formatAlertDate(approvalProcess.updatedAt),
          }
        )}
        icon={CardPin}
      />
    ) : null

  const isCurrentUserRequester = approvalProcess?.requesterId === employee.id

  const getDaysOverdue = (entry: ApprovalsV2ApprovalTimelineEntryFragment): number | undefined => {
    if (entry.status === 'running' && entry.dueOn) {
      return calculateDaysOverdue(entry.dueOn)
    }
    return undefined
  }

  const getAlertForEntry = (
    entry: ApprovalsV2ApprovalTimelineEntryFragment,
    daysOverdue: number | undefined
  ) => {
    const { status, decisionReason, time: timestamp } = entry

    if (daysOverdue !== undefined && daysOverdue > 0) {
      const isEntryAssignee =
        !isCurrentUserRequester && (entry.assignees ?? []).some((a) => a.employeeId === employee.id)

      const alertTitle = isEntryAssignee
        ? i18n.tp('procurement.purchase_requests.detail.timeline.overdue_alert.assignee.title', {
            count: daysOverdue,
          })
        : i18n.tp('procurement.purchase_requests.detail.timeline.overdue_alert.title', {
            count: daysOverdue,
          })

      return <F0Alert variant="warning" title={alertTitle} description="" />
    }

    const isRejected = status === 'rejected'
    const isChangesRequested = status === 'changesrequested'

    if (entry.actionGroup === 'approvals' && (isRejected || isChangesRequested) && decisionReason) {
      const decision = isRejected ? 'rejected' : 'changes_requested'

      const matchingReviewRequest = reviewRequests.find(
        (reviewRequest) =>
          reviewRequest.decisionReason === decisionReason && reviewRequest.decision === decision
      )

      const reviewerTime = matchingReviewRequest?.reviewedAt || timestamp

      let reviewerName = ''
      if (entry.assignees && entry.assignees.length > 0) {
        const firstAssignee = entry.assignees[0]
        if (firstAssignee?.peopleGroupName) {
          reviewerName = firstAssignee?.peopleGroupName ?? ''
        } else if (firstAssignee?.employeeId) {
          const emp = employees.find((e) => e?.id === firstAssignee?.employeeId)
          reviewerName = emp?.fullName || ''
        }
      }

      return (
        <F0Alert
          variant={isRejected ? 'critical' : 'warning'}
          title={
            isRejected
              ? i18n.t('procurement.purchase_requests.detail.timeline.rejected_alert.title', {
                  reason: decisionReason,
                })
              : i18n.t(
                  'procurement.purchase_requests.detail.timeline.changes_requested_alert.title',
                  {
                    reason: decisionReason,
                  }
                )
          }
          description={
            reviewerName
              ? `${reviewerName}, ${formatAlertDate(reviewerTime)}`
              : formatAlertDate(reviewerTime)
          }
          icon={CardPin}
        />
      )
    }

    return undefined
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="2xl">
      {automaticRejectionAlert}
      <div className="max-w-[800px]">
        <F0Box display="flex" flexDirection="column">
          {groups.map((group, groupIdx) => {
            const isLast = groupIdx === groups.length - 1

            if (group.kind === 'multi') {
              const changesRequestedEntry = group.entries.find(
                (e) =>
                  (e.status === 'changesrequested' || e.status === 'rejected') && e.decisionReason
              )
              const groupAlert = changesRequestedEntry
                ? getAlertForEntry(changesRequestedEntry, undefined)
                : undefined

              return (
                <MultiTaskGroup
                  key={`group-${group.index}`}
                  entries={group.entries}
                  buildEntryItem={(entry, _idx, isEntryLast) => ({
                    status: mapTimelineStatus(entry.status),
                    icon: getEntryIcon(entry.type, entry.actionGroup),
                    title: entry.title,
                    description: formatEntryDescription(entry.time, entry.status),
                    metadata: buildMetadata(entry, employees),
                    hideStatus: true,
                    isLast: isEntryLast,
                  })}
                  alert={groupAlert}
                  isLast={isLast}
                />
              )
            }

            const entry = group.entry

            if (entry.type === 'cannot_resolve') {
              return (
                <F0TimelineRow
                  key={`single-${group.index}`}
                  status="not-started"
                  icon={ArrowCycle}
                  title={i18n.t('approvals_v2.timeline.cannot_resolve.title')}
                  description={i18n.t('approvals_v2.timeline.cannot_resolve.description')}
                  isLast={isLast}
                />
              )
            }

            const daysOverdue = getDaysOverdue(entry)
            const alert = getAlertForEntry(entry, daysOverdue)

            return (
              <F0Box key={`single-${group.index}`} display="flex" flexDirection="column">
                <F0TimelineRow
                  status={mapTimelineStatus(entry.status)}
                  icon={getEntryIcon(entry.type, entry.actionGroup)}
                  title={entry.title}
                  description={formatEntryDescription(entry.time, entry.status)}
                  metadata={buildMetadata(entry, employees)}
                  isLast={isLast}
                />
                {alert}
              </F0Box>
            )
          })}
        </F0Box>
      </div>
    </F0Box>
  )
}

export default PurchaseRequestWorkflowTimeline
